/**
 * Generic pole-side distribution gear: the tiles behind the picker's quick
 * picks for media converters, DC supplies, PoE injectors and pole enclosures.
 *
 * Representative units, not producer SKUs (manufacturer 'Generic', never
 * verified). Every figure here is one the installer would state when
 * ordering "a 12 V / 5 A supply" or "a single-mode SC converter", and each
 * is editable on the placed device. Figures a datasheet would have to
 * supply (launch power, sensitivity, per-output fuse rating) are left OUT,
 * so the checks that need them say "assumed" or stay quiet rather than
 * judge against an invented number.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const POLE_DISTRIBUTION_GENERICS: EquipmentCatalogEntry[] = [
  {
    id: 'gen-mc-sm-sc',
    manufacturer: 'Generic',
    model: 'SM media converter, SC',
    category: 'media-converter',
    tagline: 'Generic single-mode converter, fixed SC optic, 1 x RJ45 1G, DIN rail (representative, not a producer SKU)',
    verified: false,
    specs: {
      fiberMode: 'SM',
      fiberConnector: 'SC',
      wavelengthNm: 1310,
      fiberPorts: 1,
      copperPorts: 1,
      copperSpeed: '1G',
      inputVoltage: '12-48 V DC',
      formFactor: 'din-rail',
    },
  },
  {
    id: 'gen-mc-sfp',
    manufacturer: 'Generic',
    model: 'Media converter, SFP slot',
    category: 'media-converter',
    tagline: 'Generic converter with an SFP cage, 1 x RJ45 1G, DIN rail; the SFP module sets the optics (representative)',
    verified: false,
    specs: {
      fiberMode: 'SM',
      fiberConnector: 'SFP-slot',
      fiberPorts: 1,
      copperPorts: 1,
      copperSpeed: '1G',
      inputVoltage: '12-48 V DC',
      formFactor: 'din-rail',
    },
  },
  {
    id: 'gen-mc-poe',
    manufacturer: 'Generic',
    model: 'PoE media converter, SC',
    category: 'media-converter',
    tagline: 'Generic single-mode converter with 802.3at PoE out on the copper port, DIN rail (representative)',
    verified: false,
    specs: {
      fiberMode: 'SM',
      fiberConnector: 'SC',
      wavelengthNm: 1310,
      fiberPorts: 1,
      copperPorts: 1,
      copperSpeed: '1G',
      poeOutW: 30,
      poeOutStandard: '802.3at',
      inputVoltage: '48-56 V DC',
      formFactor: 'din-rail',
    },
  },
  {
    id: 'gen-psu-12v-5a',
    manufacturer: 'Generic',
    model: '12 V / 5 A supply, 4 outputs',
    category: 'power-supply',
    tagline: 'Generic 12 V DC supply, 5 A across 4 fused outputs, in its own enclosure (representative, not a producer SKU)',
    verified: false,
    specs: {
      outputVoltageV: 12,
      outputA: 5,
      channels: 4,
      inputVoltage: '230 V AC',
      formFactor: 'enclosure',
      enclosureIncluded: true,
    },
  },
  {
    id: 'gen-psu-24v-2a5',
    manufacturer: 'Generic',
    model: '24 V / 2.5 A supply, DIN',
    category: 'power-supply',
    tagline: 'Generic 24 V DC supply, 2.5 A single output, DIN rail (representative)',
    verified: false,
    specs: {
      outputVoltageV: 24,
      outputA: 2.5,
      channels: 1,
      inputVoltage: '230 V AC',
      formFactor: 'din-rail',
    },
  },
  {
    id: 'gen-psu-48v-2a',
    manufacturer: 'Generic',
    model: '48 V / 2 A supply, DIN',
    category: 'power-supply',
    tagline: 'Generic 48 V DC supply for PoE injectors and converters, 2 A single output, DIN rail (representative)',
    verified: false,
    specs: {
      outputVoltageV: 48,
      outputA: 2,
      channels: 1,
      inputVoltage: '230 V AC',
      formFactor: 'din-rail',
    },
  },
  {
    id: 'gen-inj-at-30w',
    manufacturer: 'Generic',
    model: 'PoE+ injector 30 W',
    category: 'poe-injector',
    tagline: 'Generic single-port 802.3at injector, 30 W, gigabit pass-through, mains brick (representative)',
    verified: false,
    specs: {
      ports: 1,
      poeStandard: '802.3at',
      poePerPortW: 30,
      poeBudgetW: 30,
      dataSpeed: '1G',
      inputVoltage: '100-240 V AC',
      formFactor: 'brick',
    },
  },
  {
    id: 'gen-inj-bt-60w',
    manufacturer: 'Generic',
    model: 'PoE++ injector 60 W',
    category: 'poe-injector',
    tagline: 'Generic single-port 802.3bt Type 3 injector, 60 W, gigabit pass-through, mains brick (representative)',
    verified: false,
    specs: {
      ports: 1,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      poeBudgetW: 60,
      dataSpeed: '1G',
      inputVoltage: '100-240 V AC',
      formFactor: 'brick',
    },
  },
  {
    id: 'gen-enclosure-300x400',
    manufacturer: 'Generic',
    model: 'Pole enclosure 300 x 400, IP66',
    category: 'pole-enclosure',
    tagline: 'Generic polycarbonate pole box 300 x 400 x 170 mm, IP66, 12 TE DIN rail, pole or wall mount (representative)',
    verified: false,
    specs: {
      widthMm: 300,
      heightMm: 400,
      depthMm: 170,
      ipRating: 'IP66',
      material: 'polycarbonate',
      dinRailTE: 12,
      mounting: ['pole', 'wall'],
      lock: true,
    },
  },
  {
    id: 'gen-enclosure-400x600',
    manufacturer: 'Generic',
    model: 'Pole enclosure 400 x 600, IP66',
    category: 'pole-enclosure',
    tagline: 'Generic polycarbonate pole box 400 x 600 x 200 mm, IP66, 24 TE DIN rail, pole or wall mount (representative)',
    verified: false,
    specs: {
      widthMm: 400,
      heightMm: 600,
      depthMm: 200,
      ipRating: 'IP66',
      material: 'polycarbonate',
      dinRailTE: 24,
      mounting: ['pole', 'wall'],
      lock: true,
    },
  },
];
